import { IconButton } from '@ads/components-react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SerieLangViewModel } from '@/shared/api/generated';
import { DataTable } from '@/shared/ui/organisms/DataTable';

export interface SerieLangTableProps {
  readonly data: readonly SerieLangViewModel[];
  readonly isLoading: boolean;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly totalCount: number;
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly emptyStateAction?: ReactNode;
  readonly onEdit: (lang: SerieLangViewModel) => void;
  readonly onDelete: (lang: SerieLangViewModel) => void;
}

/**
 * Tabela de Idiomas da Série. Colunas Idioma, Título e Ações — editar/excluir
 * como ícones diretos, sem menu "...". Fiel ao frame *Idioma da Série ·
 * Listagem* de `design/series.pen`.
 */
export function SerieLangTable({
  data,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  emptyStateAction,
  onEdit,
  onDelete,
}: SerieLangTableProps) {
  const { t } = useTranslation('series');

  const columns = useMemo<ColumnDef<SerieLangViewModel, unknown>[]>(
    () => [
      {
        accessorKey: 'cultureInfo',
        header: t('langs.table.columns.language'),
        cell: info => <span className="ts-paragraph-xxs">{(info.getValue() as string) ?? ''}</span>,
      },
      {
        accessorKey: 'title',
        header: t('langs.table.columns.title'),
        cell: info => (
          <span className="ts-paragraph-xxs !font-medium text-ctx-content-title">
            {(info.getValue() as string) ?? ''}
          </span>
        ),
      },
      {
        id: 'actions',
        header: () => <div className="text-right">{t('langs.table.columns.actions')}</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-quark">
            <IconButton
              icon={PencilSquareIcon}
              ariaLabel={t('langs.table.edit')}
              hierarchy="tertiary"
              size="md"
              onClick={() => onEdit(row.original)}
            />
            <IconButton
              icon={TrashIcon}
              ariaLabel={t('langs.table.delete')}
              hierarchy="tertiary"
              className="!text-base-danger-600"
              size="md"
              onClick={() => onDelete(row.original)}
            />
          </div>
        ),
      },
    ],
    [t, onEdit, onDelete],
  );

  return (
    <DataTable
      data={data as SerieLangViewModel[]}
      columns={columns}
      isLoading={isLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={pageSize}
      onPageChange={onPageChange}
      emptyStateMessage={t('langs.table.empty')}
      emptyStateDescription={t('langs.table.emptyDescription')}
      {...(emptyStateAction ? { emptyStateAction } : {})}
    />
  );
}
